import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Monitor, Wrench, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  GET_ORGANIZATION_PRICING,
  type GetOrganizationPricingResponse,
  type GetOrganizationPricingVariables,
} from '@/graphql/organizationPricing';
import { GET_PRODUCTS } from '@/graphql/products';
import type { Product } from '@/graphql/products';
import { SoftwareTab } from './pricing/SoftwareTab';
import { ServicesTab } from './pricing/ServicesTab';

type PricingSubTab = 'software' | 'services';

interface PricingTabProps {
  clientId: string;
}

export function PricingTab({ clientId }: PricingTabProps) {
  const [activeSubTab, setActiveSubTab] = useState<PricingSubTab>('software');

  // Fetch organization pricing data
  const { data: pricingData, loading: pricingLoading, error: pricingError, refetch: refetchPricing } = useQuery<
    GetOrganizationPricingResponse,
    GetOrganizationPricingVariables
  >(GET_ORGANIZATION_PRICING, {
    variables: { organizationId: clientId },
  });

  // Fetch software products for the SKU picker
  const { data: productsData } = useQuery(GET_PRODUCTS, {
    variables: { category: 'SOFTWARE', isActive: true, limit: 100 },
  });

  const pricing = pricingData?.organizationPricing;
  const softwareProducts: Product[] = productsData?.products?.items || [];

  if (pricingLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-pond dark:text-pond-light" />
        <span className="ml-2 text-chalkboard dark:text-rain-cloud">Loading pricing data...</span>
      </div>
    );
  }

  if (pricingError) {
    return (
      <div className="card">
        <div className="card-body text-center py-8">
          <p className="text-rust dark:text-salmon">Failed to load pricing data.</p>
          <button onClick={() => refetchPricing()} className="btn-secondary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const subTabs: { key: PricingSubTab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'software',
      label: 'Software',
      icon: <Monitor className="w-4 h-4" />,
    },
    {
      key: 'services',
      label: 'Services',
      icon: <Wrench className="w-4 h-4" />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate dark:text-mist">Pricing</h2>
        <p className="text-sm text-chalkboard dark:text-rain-cloud">
          Manage software agreements and service pricing for this client
        </p>
      </div>

      {/* Sub-tab Navigation */}
      <div className="border-b border-cloud dark:border-steel">
        <nav className="flex gap-1" aria-label="Pricing tabs">
          {subTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveSubTab(tab.key)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                activeSubTab === tab.key
                  ? 'border-pond dark:border-pond-light text-pond dark:text-pond-light'
                  : 'border-transparent text-chalkboard dark:text-rain-cloud hover:text-slate dark:hover:text-mist hover:border-cloud dark:hover:border-steel'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeSubTab === 'software' && (
        <SoftwareTab
          clientId={clientId}
          softwareConfig={pricing?.softwareConfig ?? null}
          softwareSkus={pricing?.softwareSkus ?? []}
          availableProducts={softwareProducts}
          onRefetch={refetchPricing}
        />
      )}

      {activeSubTab === 'services' && (
        <ServicesTab
          clientId={clientId}
          serviceConfig={pricing?.serviceConfig ?? null}
          customSkus={pricing?.customSkus ?? []}
          onRefetch={refetchPricing}
        />
      )}
    </div>
  );
}
